import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { H3, NumberInput, Select } from '@smartshell/ui-kit';

import { LocalizationContext } from 'localization';
import { ESettingsAliases, SettingsContext } from 'settings';

import s from './EndSession.module.scss';

export const EndSession: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { action: defaultAction, timeout: defaultTimeout },
    },
    actions: { setSetting },
  } = useContext(SettingsContext);

  const ACTION_OPTIONS = [
    { title: t('setting.endSession.action.shutdown'), value: 1 },
    { title: t('setting.endSession.action.reboot'), value: 2 },
    { title: t('setting.endSession.action.logout'), value: 3 },
  ];

  const [action, setAction] = useState(defaultAction);
  const [timeout, setTimeout] = useState(defaultTimeout);

  useEffect(() => {
    setAction(defaultAction);
    setTimeout(defaultTimeout);
  }, [defaultAction, defaultTimeout]);

  const ref = useRef<HTMLInputElement>(null);
  const isTimeoutInRange = Number(timeout) <= 60 && Number(timeout) >= 1;

  const updateTimeout = useCallback((): void => {
    if (typeof timeout !== 'number') {
      return;
    }

    setSetting({
      alias: ESettingsAliases.SHELL_END_SESSION_TIMEOUT,
      value: (timeout * 60).toString(),
    });
  }, [timeout]);

  const selectedOption =
    ACTION_OPTIONS.find((option) => option.value === action)?.value ||
    ACTION_OPTIONS[0].value;

  return (
    <div className={s.container}>
      <H3 className={s.title}>
        {t('settings.shell.power_manager.action.title')}
      </H3>
      <span className={s.subtitle}>
        {t('settings.shell.power_manager.action.description')}
      </span>
      <div className={s.controls_block}>
        <Select
          name="action"
          value={selectedOption}
          options={ACTION_OPTIONS}
          disableClear
          onChange={(value) => {
            setAction(Number(value));
            setSetting({
              alias: ESettingsAliases.SHELL_END_SESSION_ACTION,
              value: value?.toString() as string,
            });
          }}
          placeholder={t('setting.endSession.action.placeholder')}
          className={s['select']}
          bordered
        />
        <div className={s.timeout}>
          <span className={s.timeout_text}>{t('in')}</span>
          <NumberInput
            variant="secondary"
            ref={ref}
            error={!isTimeoutInRange ? t('errors.time.incorrect') : undefined}
            value={timeout}
            onChange={(value) => setTimeout(value)}
            className={s.input}
            onEnter={() => {
              if (isTimeoutInRange) {
                updateTimeout();
              }
              ref.current?.blur();
            }}
            onBlur={() => {
              if (isTimeoutInRange) {
                updateTimeout();
              }
            }}
            rightContent={t('time.contracted.minutes')}
            bordered
          />
        </div>
      </div>
    </div>
  );
};
